/**
 * A FRASE que carrega os números de onde saiu — relatório comparativo (lote de 09/10/2026,
 * item 7). Puro, sem DOM.
 *
 * POR QUE ESTRUTURA, E NÃO TEXTO PRONTO
 * Uma análise escrita como string pronta pode citar um número que não está em lugar nenhum
 * do relatório — um arredondamento feito à mão, uma soma que ninguém confere, um "R$ 0" que
 * era −0,40. Aqui cada número da frase é uma PARTE com o valor, o formato e a FONTE (o campo
 * do modelo de onde veio, ou a diferença entre dois campos, ou uma constante declarada).
 * O texto é montado na hora de mostrar; e `tests/relatorio-analise.test.ts` resolve cada
 * fonte contra o modelo e exige: (1) o valor bate; (2) o pedaço literal da frase não tem
 * algarismo nenhum — número órfão não passa.
 *
 * Os NOMES de cenário entram como parte própria: são texto do usuário e podem ter dígitos
 * ("Cenário 2"), sem serem números da análise.
 */
import { moeda0, num, ymLabel } from '../../../../engine/index';

/** Os campos numéricos de cada cenário no modelo (`ModeloCenario.valores`). */
export type CampoValor =
  | 'versao'
  | 'VGV' | 'unidades' | 'permutadas' | 'CTE' | 'CTO' | 'valorTerreno'
  | 'FSF' | 'terreno' | 'obra' | 'aporte' | 'garantias' | 'PJ' | 'total' | 'gapCTO' | 'cobertura'
  | 'tetoPJ' | 'pjLimite' | 'pjSemLimite' | 'pjSemLGC'
  | 'lgcDisponivel' | 'lgcPct' | 'tomadaLGC' | 'saldoLGC'
  | 'ighReal' | 'ighMin'
  | 'vendidas' | 'vendExig' | 'salesMin' | 'financiadas' | 'finExig' | 'finMin' | 'unidadesExigidas'
  | 'pctFin' | 'obraGarPct' | 'minObraPct'
  | 'ymDesejada' | 'ymNorm' | 'ymContratacao'
  | 'recursosProprios' | 'alavancagem' | 'resultado'
  | 'folgaVend' | 'folgaFin' | 'folgaLGC' | 'folgaIGH' | 'folgaMin' | 'folgaObra' | 'folgaPrazo'
  | 'nCriterios';

/** Números que a análise cita sem que venham de um cenário — cada um declarado aqui. */
export const CONSTANTES = {
  /** quantos critérios há (o "de 7") */
  totalCriterios: 7,
  /** abaixo desta folga normalizada, o critério cumprido vira RISCO */
  limiarFolga: 0.10,
  /** o "Cumprimento de demanda" a este número de meses (ou menos) do normativo vira risco */
  mesesPerto: 1,
  /** o fator do IGH estático (mínimo = 1,30 × PJ) — escrito na planilha da Caixa */
  fatorIGH: 1.3,
  /** teto normativo do PJ: 60% do VGV e 85% do CTO */
  tetoVGVPct: 60,
  tetoCTOPct: 85,
  /** quantos cenários o relatório aceita */
  minCenarios: 2,
  maxCenarios: 5,
} as const;
export type Constante = keyof typeof CONSTANTES;

export type Formato =
  | 'moeda'      // R$ 1.234 (moeda0, como a tela)
  | 'folga'      // fração → "35%" (como a folga do Guiado)
  | 'taxa'       // percentual já em % → "21,06%"
  | 'fracao'     // fração → "98,5%"
  | 'pp'         // pontos percentuais
  | 'meses'
  | 'unidades'
  | 'vezes'      // alavancagem "2,35×"
  | 'inteiro'
  | 'versao'     // "v19"
  | 'mes';       // índice de mês → "nov.26" (ymLabel, como a tela)

export interface Ref {
  cen: string;
  campo: CampoValor;
}

/** De onde o número saiu. `dif` = o 1º menos o 2º (quem monta a frase garante o sinal). */
export type Fonte = Ref | { dif: [Ref, Ref] } | { constante: Constante };

export interface Num {
  valor: number;
  formato: Formato;
  fonte: Fonte;
}

export type Parte = string | { num: Num } | { nome: string };

export interface Frase {
  partes: Parte[];
}

/* ---------------------------------------------------------------- formatação */

/* "-0" nunca: um saldo de −R$ 0,40 (dentro da tolerância do motor) arredondado é ZERO, não "−0%" nem "−R$ 0" */
const semMenosZero = (v: number): number => (v === 0 ? 0 : v);
const umaCasa = (v: number): string => num(semMenosZero(Math.round(v * 10) / 10));

export function formatar(valor: number, formato: Formato): string {
  switch (formato) {
    case 'moeda': return moeda0(Math.abs(valor) < 0.5 ? 0 : valor);
    case 'folga': return `${num(semMenosZero(Math.round(valor * 100)))}%`;
    case 'taxa': return `${num(semMenosZero(Math.round(valor * 100) / 100))}%`;
    case 'fracao': return `${umaCasa(valor * 100)}%`;
    case 'pp': return `${umaCasa(valor)} p.p.`;
    case 'meses': return valor === 1 || valor === -1 ? `${num(valor)} mês` : `${num(valor)} meses`;
    case 'unidades': return valor === 1 ? '1 unidade' : `${num(valor)} unidades`;
    case 'vezes': return `${valor.toFixed(2).replace('.', ',')}×`;
    case 'inteiro': return num(valor);
    case 'versao': return `v${valor}`;
    case 'mes': return ymLabel(valor);
  }
}

/* ---------------------------------------------------------------- montagem */

export const ref = (cen: string, campo: CampoValor): Ref => ({ cen, campo });

export function n(valor: number, formato: Formato, fonte: Fonte): { num: Num } {
  return { num: { valor, formato, fonte } };
}

export function nome(t: string): { nome: string } {
  return { nome: t };
}

export function frase(...partes: Parte[]): Frase {
  return { partes };
}

export function textoDaFrase(f: Frase): string {
  return f.partes.map((p) => (typeof p === 'string' ? p : 'num' in p ? formatar(p.num.valor, p.num.formato) : p.nome)).join('');
}

/** Junta pedaços de frase com ", " e " e " no último — "a, b e c" (ou ", " também no último, quando a frase segue com outro "e"). */
export function juntar(grupos: Parte[][], ultimo = ' e '): Parte[] {
  const saida: Parte[] = [];
  grupos.forEach((g, i) => {
    if (i > 0) saida.push(i === grupos.length - 1 ? ultimo : ', ');
    saida.push(...g);
  });
  return saida;
}
